import { searchService } from '@/modules/hub/features/Search/services/searchService';
import type { GetResourceSearchResultsRequest, GetResourceSearchResultsItem } from '@/api/getResourceSearchResults/types';

/**
 * Service layer for Resource Library to use Algolia
 * Transforms between Resource Library format and Algolia format
 */
class ResourceLibraryAlgoliaService {
  /**
   * Search resources using Algolia
   * Returns data in the format Resource Library UI expects
   */
  async searchResources(params: GetResourceSearchResultsRequest): Promise<{
    resources: GetResourceSearchResultsItem[];
    totalCount: number;
    facets: {
      resourceTypes: Array<{ id: number; name: string; resourceCount: number; icon: string | null }>;
      categories: Array<{ id: number; name: string; count: number }>;
      businessRoles: Array<{ id: number; name: string; description: string | null; count: number }>;
      trades: Array<{ id: number; name: string; icon: string | null; count: number }>;
    };
  }> {
    // Initialize with 'library' context to exclude Podcast and News
    await searchService.initialize(false, 'library');

    // Build Algolia filters from Resource Library params
    const algoliaFilters: any = {
      type: 'resources', // Only search resources, not events
    };

    // Add resource type filter
    // Note: SearchService expects a single resourceType string, not an array
    // To support multiple, we'd need to update the SearchService
    if (params.resourceTypeNames && params.resourceTypeNames.length > 0) {
      // For now, only support single resource type selection
      algoliaFilters.resourceType = params.resourceTypeNames[0];
    }
    // If no resource types specified, don't filter (show all)

    // Add category filters (array)
    if (params.categoryNames && params.categoryNames.length > 0) {
      algoliaFilters.categories = params.categoryNames;
    }

    // Add business role filters (array) - Note: Algolia might expect different field name
    if (params.businessRoleNames && params.businessRoleNames.length > 0) {
      algoliaFilters.businessRoles = params.businessRoleNames;
    }

    // Add trade filters (array)
    if (params.tradeNames && params.tradeNames.length > 0) {
      algoliaFilters.trades = params.tradeNames;
    }

    // Calculate page for Algolia (0-indexed)
    const algoliaPage = (params.page || 1) - 1;
    const hitsPerPage = params.pageSize || 10;

    // Map sortBy and sortOrder to sort option
    let sortOption = 'newest'; // default
    if (params.sortBy && params.sortOrder) {
      if (params.sortBy === 'publishStartDate' && params.sortOrder === 'desc') {
        sortOption = 'newest';
      } else if (params.sortBy === 'publishStartDate' && params.sortOrder === 'asc') {
        sortOption = 'oldest';
      } else if (params.sortBy === 'viewCount' && params.sortOrder === 'desc') {
        sortOption = 'mostViewed';
      } else if (params.sortBy === 'title' && params.sortOrder === 'asc') {
        sortOption = 'alphabetical';
      }
    }

    // Determine if we should use special keys
    const useFavoritesKey = params.showFavorites === true;
    const useSharedWithMeKey = params.showSharedWithMe === true;

    // Search Algolia - no search term since we use global search now
    const result = await searchService.searchResources(
      '',  // Empty search to get all resources with filters
      algoliaFilters,
      algoliaPage,
      hitsPerPage,
      sortOption,
      useFavoritesKey,  // Use favorites secured key if showing favorites only
      useSharedWithMeKey  // Use shared-with-me secured key if showing shared resources only
    );

    // Transform Algolia hits to Resource Library format
    const resources: GetResourceSearchResultsItem[] = result.hits.map((hit: any) => ({
      id: hit.resourceId || hit.objectID?.replace('resource_', '') || Math.random() * 100000,
      uuid: hit.uuid || '',
      slug: hit.slug || '',
      title: hit.title || '',
      searchDescription: hit.searchDescription || null,
      isPublished: hit.isPublished || false,
      thumbnailUrl: hit.thumbnail || null,
      thumbnailFileUuid: null,
      primaryIcon: hit.primaryIcon || null,
      isFeatured: hit.isFeatured || false,
      resourceType: hit.resourceType || null,
      createdOrPublishStartDate: hit.publishStartDate || hit.createdAt || null,
      viewCount: hit.viewCount || 0,
      backgroundColor: hit.backgroundColor || null,
      textColor: hit.textColor || null,
      borderColor: hit.borderColor || null,
      contentUrl: hit.url || null,
      isFavorited: false, // Will be handled later with favorites implementation
    }));

    // Helper to generate consistent IDs from strings
    const stringToId = (str: string): number => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash);
    };

    // Transform facets to Resource Library format with counts
    const facets = {
      resourceTypes: Object.entries(result.facets?.resourceType || {}).map(([name, count]) => ({
        id: stringToId(name),
        name,
        resourceCount: count as number,
        icon: null, // Icons not available from Algolia
      })),
      categories: Object.entries(result.facets?.categories || {}).map(([name, count]) => ({
        id: stringToId(name),
        name,
        count: count as number,
      })),
      businessRoles: Object.entries(result.facets?.businessRoles || {}).map(([name, count]) => ({
        id: stringToId(name),
        name,
        description: null,
        count: count as number,
      })),
      trades: Object.entries(result.facets?.trades || {}).map(([name, count]) => ({
        id: stringToId(name),
        name,
        icon: null,
        count: count as number,
      })),
    };

    return {
      resources,
      totalCount: result.total,
      facets,
    };
  }

  /**
   * Get available facets for filters from Algolia
   * Transforms Algolia facet strings into objects the UI expects
   */
  async getMetadata(): Promise<{
    resourceTypes: Array<{ id: number; name: string; resourceCount: number; icon: string | null }>;
    categories: Array<{ id: number; name: string }>;
    businessRoles: Array<{ id: number; name: string; description: string | null }>;
    trades: Array<{ id: number; name: string; icon: string | null }>;
  }> {
    // Initialize with 'library' context to exclude Podcast and News
    await searchService.initialize(false, 'library');

    // Search with empty query to get all facets for resources only
    // Request all relevant facets
    const result = await searchService.searchAll('', { type: 'resources' }, 0, 0);

    // Extract facet values from Algolia response
    const facets = result.facets || {};

    // Transform facet strings to objects with mock IDs (hash of the string for consistency)
    const stringToId = (str: string): number => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash);
    };

    return {
      resourceTypes: Object.keys(facets.resourceType || {}).map(name => ({
        id: stringToId(name),
        name,
        resourceCount: facets.resourceType![name] || 0, // Use count from Algolia
        icon: null, // Icons not available from Algolia
      })),
      categories: Object.keys(facets.categories || {}).map(name => ({
        id: stringToId(name),
        name,
      })),
      businessRoles: Object.keys(facets.businessRoles || {}).map(label => ({
        id: stringToId(label),
        name: label,
        description: null,
      })),
      trades: Object.keys(facets.trades || {}).map(name => ({
        id: stringToId(name),
        name,
        icon: null,
      }))
    };
  }
}

export const resourceLibraryAlgoliaService = new ResourceLibraryAlgoliaService();
